import * as RadPopover from '@radix-ui/react-popover'
import { useAction, useAtom } from '@reatom/npm-react'
import { useState } from 'react'
import styled, { keyframes } from 'styled-components'

import { Button } from '@blade/ui-kit'
import {
  startWalletDepositFlowEvent,
  startWalletTransferFlowEvent,
  walletsModelAtom,
} from '@root/entities/wallet'
import { WalletModelSelectItem } from '@root/entities/wallet/ui/desktop/wallet-model-select-item'
import {
  setTradingWalletAction,
  tradingWalletAtom,
} from '@root/features/trade/trading'
import { useTranslation } from '@root/shared/localization'

import type { WalletModel } from '@root/entities/wallet'

type Props = {
  children: React.ReactElement
}

export const WalletsDropdownPopover: React.FC<Props> = ({ children }) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState<boolean>(false)

  const [walletsModel] = useAtom(walletsModelAtom)
  const [wallets] = useAtom(walletsModel.wallets)

  const [selectedWallet] = useAtom(tradingWalletAtom)
  const setWallet = useAction(setTradingWalletAction)
  const startWalletDepositFlow = useAction(startWalletDepositFlowEvent)
  const startWalletTransferFlow = useAction(startWalletTransferFlowEvent)

  const handleTriggerClick = () => setOpen(!open)
  const handleSelectWallet = (wallet: WalletModel) => {
    setWallet(wallet.id)
  }

  const handleTransferClick = () => {
    if (!selectedWallet) {
      return
    }
    startWalletTransferFlow({ wallet: selectedWallet })
    setOpen(false)
  }
  const handleDepositClick = () => {
    if (!selectedWallet) {
      return
    }
    startWalletDepositFlow({ wallet: selectedWallet })
    setOpen(false)
  }

  return (
    <Root open={open} onOpenChange={setOpen}>
      <Trigger onClick={handleTriggerClick} asChild>
        {children}
      </Trigger>
      <RadPopover.Portal>
        <Content side="bottom" align="end">
          <WalletItems>
            {wallets.map((wallet) => (
              <WalletItem
                key={wallet.address}
                wallet={wallet}
                active={selectedWallet === wallet}
                onClick={() => handleSelectWallet(wallet)}
                size="s"
              />
            ))}
          </WalletItems>
          <Actions>
            <ButtonAlt
              variant="solid"
              radius="rounded"
              size="h28"
              color="primary"
              disabled={selectedWallet === undefined}
              onClick={handleDepositClick}
            >
              {t('deposit')}
            </ButtonAlt>
            <ButtonAlt
              variant="soft"
              radius="rounded"
              size="h28"
              disabled={selectedWallet === undefined}
              onClick={handleTransferClick}
            >
              {t('transfer')}
            </ButtonAlt>
          </Actions>
        </Content>
      </RadPopover.Portal>
    </Root>
  )
}

const contentShow = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const Root = styled(RadPopover.Root)``
const Trigger = styled(RadPopover.Trigger)``

const Content = styled(RadPopover.Content)`
  border: 0.5px solid var(--background-3);
  background: var(--background-2);
  box-shadow: 0 12px 52px 0 rgba(0, 0, 0, 0.87);

  border-radius: 8px;
  margin-top: 4px;
  padding: 10px;
  gap: 6px;
  display: grid;

  animation-duration: 200ms;
  animation-name: ${contentShow};
`

const Actions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5px;
`

const ButtonAlt = styled(Button)`
  --button-radius-rounded: 6px;

  width: 122px;
  font-size: 10px;
  font-weight: 600;
`

const WalletItems = styled.div`
  display: grid;
  gap: 2px;
`

const WalletItem = styled(WalletModelSelectItem)`
  background: var(--background-1);
  border-radius: 6px;

  &[aria-selected='true'] {
    background: var(--appearanceA-7);
  }
  &:hover {
    background: var(--appearanceA-7);
  }
`
